"use client";

import { Layout, LayoutProps } from "skedyul-ui/ui";
import {
  transformAppStateToLayoutContent,
  getDialogContent,
} from "./_schema.layout";
import { usePathname, useRouter } from "next/navigation";
import { useParams, useSearchParams } from "next/navigation";
import { useSelector } from "react-redux";
import { type RootState } from "@store/index";
import { useTheme } from "@teispace/next-themes";
import { useChatWindow } from "@providers/chatWindow";
import { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { store } from "@store/index";
import { DialogSchemaProps } from "@schemas/dialog.schema";
import { getWorkflowRun } from "@store/features/settings/workflowSlice";
import { useBreakpoint } from "skedyul-ui/ui";
import { open as openSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { WorkflowsProvider, useStreamWorkflowRun } from "@providers/workflows";
import {
  DeploymentsProvider,
  useStreamDeploymentLogs,
} from "@providers/deployments";
import {
  ProvisionsProvider,
  useStreamProvisionUpdates,
} from "@providers/provisions";
import WorkplaceProvider from "@providers/workplace";
import { AgentChatProvider } from "@providers/agentChatProvider";
import { trpc } from "@utils/api";
import { type ModelOption } from "@schemas/chat.schema";
import {
  setSuggestedResponseModel,
  setSuggestedResponseLastMessageId,
  generateSuggestedResponse,
} from "@store/features/chat/chatSlice";
import { useModelPreference } from "@hooks/useModelPreference";
import { useSyncThreadSidebarFilter } from "@hooks/useSyncThreadSidebarFilter";
import { useFetchThreadsOnSidebarOpen } from "@hooks/useFetchThreadsOnSidebarOpen";
import { useNewThreadShortcut } from "@hooks/useNewThreadShortcut";

interface Props {
  children: React.ReactNode;
}

const openWorkflowSidebar = async (workflowRunId: string) => {
  // Initial fetch
  await store.dispatch(getWorkflowRun({ workflowRunId }));

  /**
   * open the sidebar
   */
  store.dispatch(
    openSideNavigation({
      position: "right",
      schema: "WORKFLOW_RUN",
      props: { width: "600px", mobileWidth: "80vw" },
    }),
  );
};

const openDeploymentSidebar = (deploymentId: string) => {
  store.dispatch(
    openSideNavigation({
      position: "right",
      schema: "DEPLOYMENT_LOGS",
      params: { deploymentId },
      props: {
        width: "750px",
        mobileWidth: "90vw",
      },
    }),
  );
};

const openProvisionSidebar = (provisionId: string) => {
  store.dispatch(
    openSideNavigation({
      position: "right",
      schema: "PROVISION_LOGS",
      params: { provisionId },
      props: {
        width: "600px",
        mobileWidth: "90vw",
      },
    }),
  );
};

const LayoutShell: React.FC<Props> = ({ children }) => {
  const pathname = usePathname();
  const params = useParams<{ subdomain: string }>();
  const searchParams = useSearchParams();
  const router = useRouter();
  const theme = useTheme();
  const chatWindow = useChatWindow();

  useSyncThreadSidebarFilter();
  useFetchThreadsOnSidebarOpen();
  useNewThreadShortcut();

  const { isBelowMd } = useBreakpoint("md");
  const [dialogContent, setDialogContent] = useState<DialogSchemaProps>({
    sections: [],
    title: "",
    description: "",
    actions: [],
  });

  // LLM models for suggested response in chat window
  const [llmModels, setLlmModels] = useState<ModelOption[]>([]);
  const llmModelsFetchedRef = useRef(false);
  const {
    preferredModelId,
    setPreferredModelId,
    isLoaded: isPreferenceLoaded,
  } = useModelPreference();
  const workplaceData = useSelector(
    (state: RootState) => state.workplace.workplace.data,
  );

  // Fetch LLM models when agentPersona is configured
  useEffect(() => {
    if (llmModelsFetchedRef.current) return;
    if (!workplaceData?.agentPersona) return;
    if (!isPreferenceLoaded) return;

    llmModelsFetchedRef.current = true;

    const fetchModels = async () => {
      try {
        const models = await trpc.llm.list.query();
        const formattedModels: ModelOption[] = models.map((m) => ({
          value: m.value,
          label: m.label,
          providerName: m.providerName,
        }));
        setLlmModels(formattedModels);

        // Use stored preference if valid, otherwise use first model
        if (formattedModels.length > 0) {
          const storedModelExists =
            preferredModelId &&
            formattedModels.some((m) => m.value === preferredModelId);
          const modelToSelect = storedModelExists
            ? preferredModelId
            : formattedModels[0]!.value;
          store.dispatch(setSuggestedResponseModel(modelToSelect));
        }
      } catch (error) {
        console.error("Failed to fetch LLM models:", error);
        llmModelsFetchedRef.current = false;
      }
    };

    void fetchModels();
  }, [workplaceData?.agentPersona, isPreferenceLoaded, preferredModelId]);

  // Auto-trigger suggested response for chat window
  const contactData = useSelector(
    (state: RootState) => state.chat.contact.data,
  );
  const suggestedResponse = useSelector(
    (state: RootState) => state.chat.contact.suggestedResponse,
  );
  const generationInProgressRef = useRef(false);

  const messages = contactData?.messages;
  const lastMessage = useMemo(() => {
    if (!messages || messages.length === 0) return null;
    const sorted = [...messages].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return sorted[0];
  }, [messages]);
  const lastMessageId = lastMessage?.id;

  useEffect(() => {
    // Only trigger for chat window (not conversations page)
    if (chatWindow.view === "hidden") return;
    if (!workplaceData?.agentPersona) return;
    if (!chatWindow.contactId) return;
    if (!messages || messages.length === 0) return;
    if (contactData?.id !== chatWindow.contactId) return;
    if (!lastMessage) return;
    if (generationInProgressRef.current) return;
    if (suggestedResponse.isLoading) return;
    if (suggestedResponse.lastMessageId === lastMessageId) return;

    const msgWithAuthor = lastMessage as unknown as {
      author?: { type: "WORKFLOW" | "CONTACT" | "MEMBER" };
      memberId?: string | null;
    };

    const isFromContact = msgWithAuthor.author
      ? msgWithAuthor.author.type === "CONTACT"
      : !msgWithAuthor.memberId;

    if (!isFromContact) return;

    generationInProgressRef.current = true;
    store.dispatch(setSuggestedResponseLastMessageId(lastMessageId!));

    const sortedMessages = [...messages].sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    const conversationMessages = sortedMessages.map((m) => {
      const mWithAuthor = m as unknown as {
        author?: { type: "WORKFLOW" | "CONTACT" | "MEMBER" };
      };
      const mIsFromContact = mWithAuthor.author
        ? mWithAuthor.author.type === "CONTACT"
        : !m.memberId;

      const date = new Date(m.createdAt);
      const timestamp = date.toLocaleString("en-AU", {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      return {
        role: mIsFromContact ? ("user" as const) : ("assistant" as const),
        content: m.message,
        timestamp,
      };
    });

    void store
      .dispatch(
        generateSuggestedResponse({
          contactId: chatWindow.contactId,
          modelId: suggestedResponse.selectedModelId,
          messages: conversationMessages,
        }),
      )
      .finally(() => {
        generationInProgressRef.current = false;
      });
  }, [
    chatWindow.view,
    chatWindow.contactId,
    workplaceData?.agentPersona,
    contactData?.id,
    messages,
    lastMessage,
    lastMessageId,
    suggestedResponse.isLoading,
    suggestedResponse.lastMessageId,
    suggestedResponse.selectedModelId,
  ]);

  const appState = useSelector(
    (rootState: RootState) => ({
      workplace: rootState.workplace,
      chat: rootState.chat,
      sideNavigation: rootState.sideNavigation,
      dialog: rootState.dialog,
      workflows: rootState.workflows,
      deployments: rootState.deployments,
      provisions: rootState.provisions,
      agents: rootState.agents,
      threads: rootState.threads,
      // CRM scoped slices drive EDIT_FIELD / EDIT_MODEL / EDIT_RELATIONSHIP sidebars.
      crmField: rootState.crm.field,
      crmModel: rootState.crm.model,
      crmRelationship: rootState.crm.relationship,
      crmNormalizations: rootState.crm.normalizations,
    }),
    (prev, next) => {
      return (
        prev.workplace === next.workplace &&
        prev.chat === next.chat &&
        prev.sideNavigation === next.sideNavigation &&
        prev.dialog === next.dialog &&
        prev.workflows === next.workflows &&
        prev.deployments === next.deployments &&
        prev.provisions === next.provisions &&
        prev.agents === next.agents &&
        prev.threads === next.threads &&
        prev.crmField === next.crmField &&
        prev.crmModel === next.crmModel &&
        prev.crmRelationship === next.crmRelationship &&
        prev.crmNormalizations === next.crmNormalizations
      );
    },
  );

  // Snapshot full store at render/callback time instead of subscribing to root
  // state (which would rerender LayoutShell on every Redux update).
  // transform to content
  const content = transformAppStateToLayoutContent(
    appState,
    store.getState(),
    isBelowMd,
    theme,
    undefined as unknown as never,
    {
      pathname,
      params,
      router,
    },
    chatWindow,
    llmModels,
    setPreferredModelId,
  );

  // Load dialog content when dialog is open or schema changes
  const loadDialogContent = useCallback(async () => {
    if (appState.dialog.open && appState.dialog.schema) {
      const dynamicContent = await getDialogContent(
        appState,
        store.getState(),
        appState.dialog,
        undefined as unknown as never,
        router,
      );
      setDialogContent(dynamicContent);
    }
  }, [appState, router]);

  useEffect(() => {
    void loadDialogContent();
  }, [loadDialogContent]);

  // Merge dialog content with the main content
  const mergedContent: LayoutProps = {
    ...content,
    dialog: content.dialog
      ? {
          ...content.dialog,
          ...dialogContent,
        }
      : undefined,
  };

  const workflowRunId = searchParams.get("run");
  const deploymentId = searchParams.get("deployment");
  const provisionId = searchParams.get("provision");

  /**
   * if a workflow run id is present, fetch the workflow run and its tasks
   * and open the sidebar
   */
  useEffect(() => {
    if (workflowRunId) {
      void openWorkflowSidebar(workflowRunId);
    }
  }, [workflowRunId]);

  /**
   * if a deployment id is present, open the deployment sidebar
   */
  useEffect(() => {
    if (deploymentId) {
      openDeploymentSidebar(deploymentId);
    }
  }, [deploymentId]);

  /**
   * if a provision id is present, open the provision sidebar
   */
  useEffect(() => {
    if (provisionId) {
      openProvisionSidebar(provisionId);
    }
  }, [provisionId]);

  // Stream deployment logs - use deploymentId from URL directly
  // This ensures we start streaming immediately when the URL has ?deployment=xxx
  // Also check if sidebar is open with DEPLOYMENT_LOGS schema for when opened via other means
  const deploymentSidebarId =
    deploymentId ||
    (appState.sideNavigation.right?.schema === "DEPLOYMENT_LOGS"
      ? (appState.sideNavigation.right?.params?.deploymentId as
          | string
          | undefined)
      : undefined);

  useStreamDeploymentLogs(deploymentSidebarId);

  // Stream provision updates - use provisionId from URL directly
  // Also check if sidebar is open with PROVISION_LOGS schema for when opened via other means
  const provisionSidebarId =
    provisionId ||
    (appState.sideNavigation.right?.schema === "PROVISION_LOGS"
      ? (appState.sideNavigation.right?.params?.provisionId as
          | string
          | undefined)
      : undefined);

  useStreamProvisionUpdates(provisionSidebarId);

  // Subscribe via provider hook; refresh the run on updates
  const callUseStreamWorkflowRun: (
    id?: string | null,
    cb?: (data: unknown) => void,
  ) => void = useStreamWorkflowRun as unknown as (
    id?: string | null,
    cb?: (data: unknown) => void,
  ) => void;

  callUseStreamWorkflowRun(workflowRunId, () => {
    if (workflowRunId) {
      void store.dispatch(getWorkflowRun({ workflowRunId }));
    }
  });

  // Check if we're on a report generation page - render minimal layout without side nav
  const isReportGeneratePage = pathname?.match(/\/reports\/[^/]+\/generate/);
  const isThreadRoute = /\/threads(\/|$)/.test(pathname ?? "");

  if (isReportGeneratePage) {
    return <div className="min-h-screen bg-background">{children}</div>;
  }

  return (
    <Layout {...mergedContent} lockPageScroll={isThreadRoute}>
      {isThreadRoute ? (
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          {children}
        </div>
      ) : (
        <div
          key={pathname}
          className="h-full min-h-full animate-nav-content-in"
        >
          {children}
        </div>
      )}
    </Layout>
  );
};

const RootLayout: React.FC<Props> = ({ children }) => {
  return (
    <WorkplaceProvider>
      <WorkflowsProvider>
        <DeploymentsProvider>
          <ProvisionsProvider>
            <AgentChatProvider>
              <LayoutShell>{children}</LayoutShell>
            </AgentChatProvider>
          </ProvisionsProvider>
        </DeploymentsProvider>
      </WorkflowsProvider>
    </WorkplaceProvider>
  );
};

export default RootLayout;
